import { isValidIsoDate } from '@cattle/money'
import { z } from 'zod'

/**
 * Date-only fields (`purchaseDate`, `incurredOn`, `saleDate`) are calendar
 * dates, not instants — "bought on 14 June" is true regardless of the hour or
 * the reader's timezone. They travel as `YYYY-MM-DD` text.
 *
 * The regex alone is not enough: `2026-02-31` and `2026-13-01` both match it.
 * `isValidIsoDate` (from @cattle/money, one implementation) round-trips the
 * string through a real calendar, so only dates that exist survive.
 *
 * The result is **branded**. `z.infer` gives `string & { IsoDate }`, so an
 * instant, a month or an arbitrary string cannot be assigned to a date-only
 * field by accident — the mistake that silently backdates a purchase. Client
 * *input* stays a plain `string` (`z.input`), so nothing has to cast to send
 * a request; only parsed output carries the brand.
 */
export const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

export const isoDateSchema = z
  .string()
  .regex(ISO_DATE_PATTERN, 'Use a YYYY-MM-DD date.')
  .refine(isValidIsoDate, 'That date does not exist.')
  .brand<'IsoDate'>()

export type IsoDateString = z.infer<typeof isoDateSchema>

/** Lift a literal into the branded type — for fixtures, mocks and tests. */
export const isoDate = (value: string): IsoDateString => isoDateSchema.parse(value)

/**
 * An instant: ISO-8601 **with an offset** (`2026-09-09T14:03:00+05:00`, or a
 * trailing `Z`). Used for `createdAt`, `unlockedUntil`, `lastSignedInAt` —
 * anything that happened at a moment rather than on a day.
 */
export const isoInstantSchema = z.iso.datetime({ offset: true })
export type IsoInstant = z.infer<typeof isoInstantSchema>

/** A billing/accounting month, `YYYY-MM`. `recurring_postings.period_month`. */
export const YEAR_MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/
export const yearMonthSchema = z.string().regex(YEAR_MONTH_PATTERN, 'Use a YYYY-MM month.')
export type YearMonth = z.infer<typeof yearMonthSchema>

/**
 * The `?from=&to=` pair every report and ledger filter shares. Both inclusive
 * on the wire; the server converts to a half-open interval internally, which
 * is the only place that convention lives.
 */
export const dateRangeQuery = z.object({
  from: isoDateSchema.optional(),
  to: isoDateSchema.optional(),
})
export type DateRangeQuery = z.input<typeof dateRangeQuery>
